import * as React from 'react';
import {useEffect, useState} from 'react';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import storageLogService from '../../services/StorageLogService';
import {Accordion, AccordionDetails, AccordionSummary, Button, Checkbox, CircularProgress, Divider, FormControlLabel, Grid2 as Grid, TextField, Tooltip} from '@mui/material';
import {useTitle} from '../dashboard/TitleContext';
import {useSnackbar} from "../../util/SnackbarProvider";
import IconButton from "@mui/material/IconButton";
import {ArrowDropDownIcon} from "@mui/x-date-pickers";
import {ArrowDropUp, FilterAltOff, KeyboardArrowDown} from "@mui/icons-material";
import {limit} from "../../util/StringUtils";
import FilterPanel from "../common/FilterPanel";

const ALL_COLUMNS = [
    {id: 'id', label: 'ID', head: <></>, width: "60px"},
    {id: 'username', label: 'Név'},
    {id: 'operationLabel', label: 'Művelet', width: '150px'},
    {id: 'productName', label: 'Termék', width: "400px", fun: d => <Tooltip title={d} placement="left">{limit(d, 50)}</Tooltip>},
    {id: 'productManufacturer', label: 'Gyártó'},
    {id: 'subStorageFrom', label: 'Forrás'},
    {id: 'subStorageTo', label: 'Cél'},
    {id: 'change', label: 'Változás', width: "300px", fun: (v, r) => formatChange(r)},
    {id: 'createdAt', label: 'Dátum', head: <></>, width: "150px"},
];

const DEFAULT_COLUMNS = ['id', 'username', 'operationLabel', 'productName', 'productManufacturer', 'subStorageFrom', 'subStorageTo', 'change', 'createdAt'];

const operationMap = {
    "CREATE_PRODUCT": {label: "Termék létrehozása", color: "#B4E380"},
    "CREATE_PRODUCT_ITEM": {label: "Termék példányok bevételezése", color: "#B4E380"},
    "UPDATE_PRODUCT": {label: "Termék szerkesztése", color: "#F6FB7A"},
    "UPDATE_PRODUCT_ITEM": {label: "Termék példány szerkesztése", color: "#F6FB7A"},
    "MOVE_PRODUCT_ITEM_WITH_AMOUNT_INCREASE": {label: "Termék mozgatása", color: "#7BD3EA"},
    "MOVE_PRODUCT_ITEM": {label: "Termék mozgatása", color: "#7BD3EA"},
    "DISCARD_PRODUCT_ITEMS": {label: "Termék példány leselejtezése", color: "#FA7070"},
    "CORRECT_PRODUCT_ITEM_AMOUNT": {label: "Termék példány mennyiség korrekció", color: "#CF9FFF"}
};

const LOCAL_STORAGE_ITEM_NAME = "storage-logs-rows-per-page";
const LOCAL_STORAGE_SEARCH_PARAMS = "storage-log-search-params"
const LOCAL_STORAGE_COLUMN_NAMES = "storage-log-column-names"
const DEFAULT_SEARCH_PARAMS = {}

export default function StorageLogTable() {

    useTitle([{name: 'Raktár Történet'}])

    const [tableData, setTableData] = useState({
        page: 0,
        rowsPerPage: localStorage.getItem(LOCAL_STORAGE_ITEM_NAME) || 25,
        totalCount: 0
    })
    const {sendAlertMessage} = useSnackbar()
    const [searchParams, setSearchParams] = useState(JSON.parse(localStorage.getItem(LOCAL_STORAGE_SEARCH_PARAMS)) || DEFAULT_SEARCH_PARAMS)
    const [columnNames, setColumnNames] = React.useState(JSON.parse(localStorage.getItem(LOCAL_STORAGE_COLUMN_NAMES)) || DEFAULT_COLUMNS)
    const filterPanelRef = React.useRef();
    const [expanded, setExpanded] = useState(true);

    useEffect(() => {
        storageLogService.search(buildQueryParams())
            .then(response => {
                const result = response.content.map((log) => {
                    const {label: operationLabel, color} = operationMap[log.operation] || {label: "", color: ""};
                    return {...log, operationLabel: operationLabel, color: color}
                })
                setTableData((prev) => ({...prev, totalCount: response.totalElements, rows: result}))
            })
            .catch(error => {
                sendAlertMessage(error?.message || "Raktár történet lekérése sikertelen", 'error')
                setTableData((prev) => ({...prev, rows: []}))
            })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tableData.page, tableData.rowsPerPage, searchParams]);

    function buildQueryParams() {
        const filteredSearchParams = searchParams ? Object.fromEntries(Object.entries(searchParams).filter(([, value]) => value !== '')) : {};
        const operation = findOperationKeyByText(searchParams.operationLabel)
        return {
            page: tableData.page,
            size: tableData.rowsPerPage,
            ...filteredSearchParams,
            ...(operation && {operation: operation}),
            ...(searchParams?.fromDate && {fromDate: searchParams.fromDate + "T00:00:00"}),
            ...(searchParams?.toDate && {toDate: searchParams.toDate + "T00:00:00"}),
        }
    }

    const onSearchParamsChange = (event) => {
        const {name, value} = event.target
        setSearchParams((prev) => ({
            ...prev,
            [name]: value
        }))
    }

    function findOperationKeyByText(text) {
        if(!text) return
        const search = text.toLowerCase();
        for (const [key, {label}] of Object.entries(operationMap)) {
            if (label?.toLowerCase().includes(search)) {
                return key;
            }
        }
        return 'NONE';
    }

    const onChangeCheckBox = (event) => {
        const {name, checked} = event.target;
        setSearchParams((prev) => ({
            ...prev,
            [name]: checked,
        }));
    };

    const onChangeColumnCheckBox = (event) => {
        const {checked, name} = event.target;
        setColumnNames((prev) => {
            return checked ? prev.includes(name) ? prev : [...prev, name] : prev.filter((col) => col !== name);
        });
    };

    const clear = () => {
        if (searchParams != null) sendAlertMessage('Szűrők alaphelyzetbe állítva', 'info')
        setSearchParams(DEFAULT_SEARCH_PARAMS)
        filterPanelRef.current?.resetSelection();
    }

    useEffect(() => {
        if (searchParams) localStorage.setItem(LOCAL_STORAGE_SEARCH_PARAMS, JSON.stringify(searchParams));
        else localStorage.removeItem(LOCAL_STORAGE_SEARCH_PARAMS)
    }, [searchParams]);

    useEffect(() => {
        if (columnNames) localStorage.setItem(LOCAL_STORAGE_COLUMN_NAMES, JSON.stringify(columnNames));
        else localStorage.removeItem(LOCAL_STORAGE_COLUMN_NAMES)
    }, [columnNames]);

    return (
        <>
            <Accordion style={{marginBottom: "10px"}} disableGutters expanded={expanded}
                       onChange={() => setExpanded(!expanded)}>
                <AccordionSummary
                    expandIcon={<KeyboardArrowDown sx={{marginRight: "0px", height: "2em", width: "auto"}}/>} sx={{
                    minHeight: "0px",
                    fontSize: "24px",
                    fontWeight: "bold",
                    backgroundColor: "darkgray",
                    borderRadius: "4px 4px 0px 0px",
                    paddingLeft: "20px",
                    margin: "0"
                }}>
                    Keresési Feltételek
                </AccordionSummary>
                <AccordionDetails className="filter-panel">
                    <Grid container columns={10} spacing={2} padding={"10px"}>
                        <Grid size={{xs: 7}}>
                            <Grid container columns={20} spacing={2} padding={"10px"}>

                                <Grid size={{lg: 5, md: 10, xs: 20}}>
                                    <TextField
                                        variant="outlined"
                                        margin="normal"
                                        fullWidth
                                        type="date"
                                        slotProps={{inputLabel: {shrink: true}}}
                                        label="-tól"
                                        name="fromDate"
                                        autoComplete="off"
                                        onChange={onSearchParamsChange}
                                        sx={{marginTop: "0px"}}
                                        value={(searchParams && searchParams.fromDate) || "-1"}
                                    />
                                </Grid>

                                <Grid size={{lg: 5, md: 10, xs: 20}}>
                                    <TextField
                                        variant="outlined"
                                        margin="normal"
                                        fullWidth
                                        type="date"
                                        slotProps={{inputLabel: {shrink: true}}}
                                        label="-ig"
                                        name="toDate"
                                        autoComplete="off"
                                        onChange={onSearchParamsChange}
                                        sx={{marginTop: "0px"}}
                                        value={(searchParams && searchParams.toDate) || "-1"}
                                    />
                                </Grid>

                                <Grid size={{xs: 20}}><Divider/></Grid>

                                <Grid size={{xs: 20}}>
                                    {ALL_COLUMNS.map((item, index) => (
                                        <FormControlLabel key={index}
                                                          control={<Checkbox
                                                              variant="outlined"
                                                              margin="normal"
                                                              size="large"
                                                              name={item.id}
                                                              onChange={onChangeColumnCheckBox}
                                                              checked={columnNames?.includes(item.id) || false}
                                                              sx={{padding: "0px"}}
                                                          />} label={item.label?.props?.title || item.label}
                                                          style={{paddingRight: "20px", margin: "0px"}}/>
                                    ))}
                                </Grid>

                            </Grid>
                        </Grid>
                        <Grid size={{xs: 3}}>
                            <FilterPanel
                                type="STORAGE_LOG"
                                ref={filterPanelRef}
                                currentSearchParams={searchParams}
                                onFilterSelect={(params) => setSearchParams(params)}
                            />
                        </Grid>
                    </Grid>
                </AccordionDetails>
            </Accordion>

            <Paper sx={{width: '100%', overflow: 'hidden'}}>
                <TableContainer>
                    <Table stickyHeader aria-label="sticky table" sx={{tableLayout: "fixed"}}>
                        <TableHead>
                            <TableRow>
                                <TableCell key="actions"
                                           style={{width: "50px", paddingLeft: "10px"}}
                                           align='left' padding='none'>
                                    <Tooltip title="Szűrők törlése">
                                        <Button variant='contained' color='info'
                                                sx={{minWidth: "0px", padding: "0px", marginRight: "10px"}}
                                                onClick={clear}>
                                            <FilterAltOff fontSize="large"/>
                                        </Button>
                                    </Tooltip>
                                </TableCell>
                                {ALL_COLUMNS.filter(c => columnNames.includes(c.id)).map((column, index) => (
                                    <TableCell key={index} align={column.align} sx={{width: column.width ? column.width : "auto", padding: "8px 3px"}}>
                                        {column.head ? <span style={{marginLeft: "10px"}}>{column.label}</span> :
                                            <TextField name={column.id} label={column.label} size="small"
                                                       value={searchParams?.[column.id] || ""} onChange={onSearchParamsChange}
                                                       sx={{margin: "0px", width: "calc(100% - 24px)"}} autoComplete="off"/>}
                                        <IconButton sx={{padding: "0px", marginTop: column.head ? "0px" : "8px"}} onClick={() => setSearchParams(prev => ({
                                            ...prev,
                                            orderBy: column.id,
                                            sort: searchParams.sort === 'ASC' ? 'DESC' : 'ASC'
                                        }))}>
                                            {searchParams.sort == null || searchParams.orderBy !== column.id ?
                                                <ArrowDropDownIcon sx={{color: "lightgray"}}/> :
                                                searchParams.sort === 'ASC' ? <ArrowDropDownIcon/> : <ArrowDropUp/>}
                                        </IconButton>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {tableData.rows == null ? <TableRow sx={{textAlign: "center"}}><TableCell colSpan={9} sx={{
                                    padding: "200px",
                                    textAlign: "center"
                                }}><CircularProgress/></TableCell></TableRow> :
                                tableData.rows && tableData.rows.map((row) => {
                                    return (
                                        <TableRow role="checkbox" tabIndex={-1} key={row.id}
                                                  sx={{backgroundColor: row.color}}>
                                            <TableCell></TableCell>
                                            {ALL_COLUMNS.filter(c => columnNames.includes(c.id)).map((column, index) => {
                                                const value = row[column.id];
                                                return (
                                                    <TableCell key={index} align={column.align}>
                                                        {column.fun ? column.fun(value, row) : value}
                                                    </TableCell>
                                                );
                                            })}
                                        </TableRow>
                                    );
                                })}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[3, 10, 25, 50]}
                    component="div"
                    count={tableData.totalCount}
                    rowsPerPage={parseInt(tableData.rowsPerPage)}
                    page={tableData.page}
                    onPageChange={(event, newPage) => setTableData((prev) => ({...prev, page: newPage}))}
                    onRowsPerPageChange={(event) => {
                        setTableData((prev) => ({...prev, rowsPerPage: event.target.value, page: 0}))
                        localStorage.setItem(LOCAL_STORAGE_ITEM_NAME, event.target.value)
                    }}
                    labelRowsPerPage="Elemek oldalanként:"
                />
            </Paper>
        </>
    );
}

function formatChange(row) {
    const obj = JSON.parse(row.change)

    if (row.operation === 'UPDATE_PRODUCT' || row.operation === 'UPDATE_PRODUCT_ITEM' || row.operation === 'CORRECT_PRODUCT_ITEM_AMOUNT') {

        const renderField = (field, label, suffix = '') => {
            return field ? <>{label} {'[' + field.originalValue + ' → ' + field.updatedValue + suffix + ']'}<br/></> : null;
        };

        return (<>
                {renderField(obj.amount, 'Mennyiség')}
                {renderField(obj.name, 'Név')}
                {renderField(obj.manufacturer, 'Gyártó')}
                {renderField(obj.centralStorageLocation, 'Központi Raktár Hely')}
                {renderField(obj.ean, 'EAN')}
                {renderField(obj.articleNumber, 'Cikkszám')}
                {renderField(obj.vtszSzjTeszor, 'Vtsz/Szj/TESZOR')}
                {renderField(obj.component, 'Komponensek')}
                {renderField(obj.stockManagement, 'Készletkezelés')}
                {renderField(obj.dataSheetUrl, 'Adatlap URL')}
                {renderField(obj.scoreSheetUrl, 'Teljesítménylap URL')}
                {renderField(obj.unitOfMeasurement, 'Mértékegység')}
                {renderField(obj.comment, 'Komment')}

                {renderField(obj.purchasePrice, 'Vásárlási Ár', ' Ft')}
                {renderField(obj.sellingPrice, 'Eladási Ár', ' Ft')}
                {renderField(obj.vat, 'ÁFA')}
                {renderField(obj.guarantee, 'Garancia')}
                {renderField(obj.expiryDate, 'Szavatosság Lejárta')}
                {renderField(obj.factoryNumber, 'Egyedi Gyári Szám')}
                {renderField(obj.packageManagement, 'Kiszerelés Kezelése')}
                {renderField(obj.minimumStockInStorage, 'Raktárankénti Minimum Készlet')}
            </>
        )
    } else {

        const renderField = (field, label, suffix = '') => {
            return field && <>{label + ": "} {field} {suffix}<br/></>
        };

        const title = <>
            {renderField(obj.centralStorageLocation, 'Központi Raktár Hely')}
            {renderField(obj.ean, 'EAN')}
            {renderField(obj.articleNumber, 'Cikkszám')}
            {renderField(obj.vtszSzjTeszor, 'Vtsz/Szj/TESZOR')}
            {renderField(obj.component, 'Komponensek')}
            {renderField(obj.stockManagement, 'Készletkezelés')}
            {renderField(obj.dataSheetUrl, 'Adatlap URL')}
            {renderField(obj.scoreSheetUrl, 'Teljesítménylap URL')}
            {renderField(obj.unitOfMeasurement, 'Mértékegység')}
            {renderField(obj.comment, 'Komment')}

            {renderField(obj.purchasePrice, 'Vásárlási Ár', ' Ft')}
            {renderField(obj.sellingPrice, 'Eladási Ár', ' Ft')}
            {renderField(obj.vat, 'ÁFA')}
            {renderField(obj.guarantee, 'Garancia')}
            {renderField(obj.expiryDate, 'Szavatosság Lejárta')}
            {renderField(obj.factoryNumber, 'Egyedi Gyári Szám')}
            {renderField(obj.packageManagement, 'Kiszerelés Kezelése')}
            {renderField(obj.minimumStockInStorage, 'Raktárankénti Minimum Készlet')}
        </>
        const showTooltip = !title.props.children.every(element => element === undefined)
        const content = <>
            {renderField(obj.name, "Név")}
            {renderField(obj.manufacturer, "Gyártó")}
            {obj.originalAmount == null ? renderField(obj.amount, "Mennyiség") : ""}
            {obj.originalAmount != null ? <>Mennyiség: {obj.updatedAmount - obj.originalAmount}<br/></> : ""}
            {obj.fromStorageOriginalAmount != null ? <>Forrás
                Raktár {'[' + obj.fromStorageOriginalAmount + ' → ' + obj.fromStorageUpdatedAmount + ']'} <br/></> : ""}
            {obj.originalAmount != null ? <>Cél Raktár {'[' + obj.originalAmount + ' → ' + obj.updatedAmount + ']'}
                <br/></> : ""}
        </>
        return (
            <>
                {showTooltip ?
                    <Tooltip title={title}> {content} </Tooltip> :
                    <>{content}</>
                }
            </>
        );

    }
}